import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import api from "../../api/axios";

const number = value => Number(value || 0).toLocaleString("es-ES");

const eventLabels = {
  user_registered: "Usuarios registrados",
  binder_card_added: "Cartas agregadas",
  wanted_card_added: "Cartas buscadas",
  card_like_received: "Intereses en cartas",
  comment_created: "Comentarios",
  post_created: "Publicaciones",
  match_generated: "Matches",
  trade_completed: "Trades completados",
  trade_review_created: "Valoraciones",
  card_scanned: "Cartas escaneadas",
  trade_day_attended: "Asistencias a Trade Day"
};

function MetricTile({ label, value, tone = "ruby" }) {
  return (
    <div className={"report-metric-tile is-" + tone}>
      <span>{label}</span>
      <strong>{number(value)}</strong>
    </div>
  );
}

function CardFact({ title, fact, empty = "Sin datos suficientes todavia." }) {
  return (
    <Card className="cardora-card report-fact-card h-100">
      <Card.Body>
        <span className="report-card-eyebrow">{title}</span>
        {fact ? (
          <>
            <strong>{fact.card_name || fact.set_name || fact.name}</strong>
            <small>{[fact.set_name, fact.rarity].filter(Boolean).join(" · ")}</small>
            <Badge bg="dark">{number(fact.total || fact.likes_count || fact.activity_count || fact.wanted_count)} señales</Badge>
          </>
        ) : (
          <p className="muted-copy mb-0">{empty}</p>
        )}
      </Card.Body>
    </Card>
  );
}

function ReportsHome() {
  const [period, setPeriod] = useState("week");
  const [report, setReport] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/analytics/cardora-report", { params: { period } }).then(({ data }) => data),
      token
        ? api.get("/analytics/personal-report", { params: { period } }).then(({ data }) => data).catch(() => null)
        : Promise.resolve(null)
    ])
      .then(([communityReport, personalReport]) => {
        if (cancelled) return;
        setReport(communityReport);
        setPersonal(personalReport);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [period, token]);

  const activeEvents = useMemo(() => Object.entries(report?.eventsByType || {})
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 8), [report]);

  return (
    <Container className="page-band report-page">
      <div className="report-hero">
        <div>
          <span className="report-kicker">Pulso de comunidad</span>
          <h1 className="section-title">Reporte Cardora</h1>
          <p className="muted-copy">
            Un resumen vivo de cartas, buscadas, matches, tiendas y actividad para entender que esta pasando en la comunidad.
          </p>
        </div>
        <div className="report-period-toggle">
          <Button className={period === "week" ? "btn-cardora" : ""} variant={period === "week" ? "dark" : "outline-dark"} onClick={() => setPeriod("week")}>Semana</Button>
          <Button className={period === "month" ? "btn-cardora" : ""} variant={period === "month" ? "dark" : "outline-dark"} onClick={() => setPeriod("month")}>Mes</Button>
        </div>
      </div>

      {loading && (
        <Card className="cardora-card">
          <Card.Body className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> Generando reporte...
          </Card.Body>
        </Card>
      )}

      {!loading && !report && (
        <Card className="cardora-card">
          <Card.Body>No se pudo cargar el reporte. Revisa que la migracion de actividad este aplicada.</Card.Body>
        </Card>
      )}

      {report && (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} lg={3}><MetricTile label="Nuevos usuarios" value={report.community?.newUsers} /></Col>
            <Col sm={6} lg={3}><MetricTile tone="teal" label="Cartas agregadas" value={report.community?.cardsAdded} /></Col>
            <Col sm={6} lg={3}><MetricTile tone="gold" label="Cartas buscadas" value={report.community?.wantedAdded} /></Col>
            <Col sm={6} lg={3}><MetricTile tone="ink" label="Matches" value={report.eventsByType?.match_generated} /></Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6} xl={3}><CardFact title="Carta mas buscada" fact={report.trends?.mostWanted} /></Col>
            <Col md={6} xl={3}><CardFact title="Carta mas agregada" fact={report.trends?.mostAdded} /></Col>
            <Col md={6} xl={3}><CardFact title="Carta con mas interes" fact={report.trends?.mostLiked} /></Col>
            <Col md={6} xl={3}><CardFact title="Set mas activo" fact={report.trends?.activeSet} /></Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={7}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <span className="report-card-eyebrow">Actividad que alimenta Cardora</span>
                  <div className="report-event-list">
                    {activeEvents.length === 0 && <p className="muted-copy mb-0">Todavia no hay eventos registrados en este periodo.</p>}
                    {activeEvents.map(([key, value]) => (
                      <div className="report-event-row" key={key}>
                        <span>{eventLabels[key] || key}</span>
                        <strong>{number(value)}</strong>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <span className="report-card-eyebrow">Curiosidad</span>
                  {report.curiosity ? (
                    <p className="report-story">
                      <strong>{report.curiosity.card_name}</strong> aparece como buscada por {number(report.curiosity.wanted_count)} usuarios y solo {number(report.curiosity.offered_count)} la tienen disponible en binders publicos.
                    </p>
                  ) : (
                    <p className="muted-copy">Cuando haya mas binders y buscadas, Cardora empezara a contar historias utiles de la comunidad.</p>
                  )}
                  <div className="report-cost-note">
                    <span>Cartas digitalizadas</span>
                    <strong>{number(report.digitization?.scans)} escaneos en este periodo</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <span className="report-card-eyebrow">Tiendas con movimiento</span>
                  <div className="report-list">
                    {(report.top_businesses || []).length === 0 && <p className="muted-copy mb-0">Sin actividad de tiendas todavia.</p>}
                    {(report.top_businesses || []).map(store => (
                      <Link to={`/store/${store.slug}`} key={store.id} className="report-link-row">
                        <span>{store.name}</span>
                        <strong>{number(store.activity_count)}</strong>
                      </Link>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <span className="report-card-eyebrow">Usuarios mejor valorados</span>
                  <div className="report-list">
                    {(report.top_reviewed || []).length === 0 && <p className="muted-copy mb-0">Aun faltan trades valorados.</p>}
                    {(report.top_reviewed || []).map(user => (
                      <Link to={`/u/${user.handle || user.id}`} key={user.id} className="report-link-row">
                        <span>{user.display_name}</span>
                        <strong>{user.rating} estrellas</strong>
                      </Link>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {token && personal && (
        <Card className="cardora-card report-personal-card">
          <Card.Body>
            <span className="report-card-eyebrow">Tu progreso</span>
            <h2>{period === "month" ? "Tu mes" : "Tu semana"} en Cardora</h2>
            <Row className="g-3">
              <Col sm={6} lg={3}><MetricTile tone="ruby" label="Cartas agregadas" value={personal.summary?.cardsAdded} /></Col>
              <Col sm={6} lg={3}><MetricTile tone="teal" label="Buscadas nuevas" value={personal.summary?.wantedAdded} /></Col>
              <Col sm={6} lg={3}><MetricTile tone="gold" label="Valoraciones positivas" value={personal.summary?.positiveReviews} /></Col>
              <Col sm={6} lg={3}><MetricTile tone="ink" label="Personas con tus buscadas" value={personal.summary?.newPeopleWithWantedCards} /></Col>
            </Row>
            {(personal.wanted_opportunities || []).length > 0 && (
              <div className="report-personal-opportunities">
                <strong>Nuevas oportunidades para tus buscadas</strong>
                {(personal.wanted_opportunities || []).map(item => (
                  <span key={`${item.card_name}-${item.set_name}`}>{item.card_name} · {item.people_count} personas</span>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {!token && (
        <Card className="cardora-card report-personal-card">
          <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <span className="report-card-eyebrow">Reporte personal</span>
              <h2>Registra tu binder para ver tu progreso semanal</h2>
            </div>
            <Button as={Link} to="/register" className="btn-cardora">Crear cuenta</Button>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default ReportsHome;
